import React, { useState } from 'react';
import { InterviewChat } from '../../components/chat/InterviewChat';
import { StartInterviewModal } from '../../components/chat/StartInterviewModal';
import { InterviewSession, InterviewSessionSummary } from '../../types/chat';
import { Button } from '../../components/ui/Button';

const MsIcon: React.FC<{ name: string; className?: string; fill?: boolean }> = ({
  name,
  className = '',
  fill = false,
}) => (
  <span
    className={`material-symbols-outlined ${className}`}
    style={{ fontVariationSettings: `'FILL' ${fill ? 1 : 0}, 'wght' 500` }}
  >
    {name}
  </span>
);

export const InterviewTraining: React.FC = () => {
  const [currentSession, setCurrentSession] = useState<InterviewSession | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sessionSummary, setSessionSummary] = useState<InterviewSessionSummary | null>(null);

  const handleStartInterview = (session: InterviewSession) => {
    setCurrentSession(session);
    setSessionSummary(null);
  };

  const handleSessionUpdate = (session: InterviewSession) => {
    setCurrentSession(session);
  };

  const handleSessionEnd = (summary: InterviewSessionSummary | null) => {
    setSessionSummary(summary);
    setCurrentSession(null);
  };

  const handleNewSession = () => {
    setIsModalOpen(true);
    setSessionSummary(null);
  };

  if (currentSession) {
    return (
      <div className="flex min-h-[calc(100dvh-8rem)] flex-col bg-stitch-canvas px-4 py-4 sm:px-6 lg:px-8">
        <div className="mx-auto flex h-[min(92dvh,calc(100dvh-8rem))] min-h-[520px] w-full max-w-7xl flex-col">
          <div className="min-h-0 flex-1">
            <InterviewChat
              session={currentSession}
              onSessionUpdate={handleSessionUpdate}
              onSessionEnd={handleSessionEnd}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stitch-canvas">
      <div className="border-b border-stitch-border bg-gradient-to-br from-stitch-primary-soft via-stitch-surface to-stitch-accent-soft">
        <div className="mx-auto max-w-7xl px-4 py-10 text-center sm:px-6 md:text-left lg:px-8">
          <p className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-stitch-primary md:justify-start">
            <MsIcon name="smart_toy" className="text-base" />
            AI coaching
          </p>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-stitch-fg md:text-4xl">AI Interview Training</h1>
          <p className="mx-auto mt-3 max-w-2xl text-lg text-stitch-fg-soft md:mx-0">
            Practice your interview skills with our AI-powered mock interviews. Get real-time feedback and improve your
            performance.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {sessionSummary && (
          <div className="mb-8 rounded-stitch-xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-card">
            <div className="mb-4 flex items-center gap-2">
              <MsIcon name="emoji_events" className="text-stitch-warning" fill />
              <h2 className="text-xl font-semibold text-stitch-fg">Session complete!</h2>
            </div>

            <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-3">
              <div className="text-center">
                <div className="rounded-stitch-lg bg-stitch-info-soft p-4">
                  <MsIcon name="bar_chart" className="mx-auto mb-2 text-3xl text-stitch-info" />
                  <div className="text-2xl font-bold text-stitch-info">
                    {sessionSummary.averageScore.toFixed(1)}/10
                  </div>
                  <div className="text-sm text-stitch-muted">Average score</div>
                </div>
              </div>

              <div className="text-center">
                <div className="rounded-stitch-lg bg-stitch-success-soft p-4">
                  <MsIcon name="schedule" className="mx-auto mb-2 text-3xl text-stitch-success" />
                  <div className="text-2xl font-bold text-stitch-success">
                    {Math.floor(parseInt(sessionSummary.duration, 10) / 60)}m
                  </div>
                  <div className="text-sm text-stitch-muted">Duration</div>
                </div>
              </div>

              <div className="text-center">
                <div className="rounded-stitch-lg bg-stitch-accent-soft p-4">
                  <MsIcon name="quiz" className="mx-auto mb-2 text-3xl text-stitch-accent" />
                  <div className="text-2xl font-bold text-stitch-accent">{sessionSummary.totalQuestions}</div>
                  <div className="text-sm text-stitch-muted">Questions</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <MsIcon name="thumb_up" className="text-stitch-success" fill />
                  <h3 className="font-semibold text-stitch-fg">Strengths</h3>
                </div>
                <ul className="space-y-2">
                  {sessionSummary.strengthAreas.map((strength, index) => (
                    <li key={index} className="flex items-center text-sm text-stitch-fg-soft">
                      <div className="mr-2 h-2 w-2 rounded-full bg-stitch-success" />
                      {strength}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="mb-3 flex items-center gap-2">
                  <MsIcon name="trending_up" className="text-stitch-warning" />
                  <h3 className="font-semibold text-stitch-fg">Areas for improvement</h3>
                </div>
                <ul className="space-y-2">
                  {sessionSummary.improvementAreas.map((area, index) => (
                    <li key={index} className="flex items-center text-sm text-stitch-fg-soft">
                      <div className="mr-2 h-2 w-2 rounded-full bg-stitch-warning" />
                      {area}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-6 border-t border-stitch-border pt-6">
              <div className="mb-3 flex items-center gap-2">
                <MsIcon name="lightbulb" className="text-stitch-primary" />
                <h3 className="font-semibold text-stitch-fg">Overall feedback</h3>
              </div>
              <ul className="space-y-2">
                {sessionSummary.overallFeedback.map((feedback, index) => (
                  <li key={index} className="text-sm text-stitch-fg-soft">
                    • {feedback}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="rounded-stitch-xl border border-stitch-border bg-stitch-surface p-8 text-center shadow-stitch-card">
          {!sessionSummary ? (
            <>
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-stitch-primary-soft">
                <MsIcon name="play_circle" className="text-4xl text-stitch-primary" fill />
              </div>
              <h2 className="mb-4 text-2xl font-semibold text-stitch-fg">Ready to practice?</h2>
              <p className="mx-auto mb-6 max-w-md text-stitch-fg-soft">
                Start your AI-powered mock interview session. Choose your interview type, experience level, and specific
                role to get personalized questions.
              </p>
            </>
          ) : (
            <>
              <h2 className="mb-4 text-2xl font-semibold text-stitch-fg">Ready for another round?</h2>
              <p className="mx-auto mb-6 max-w-md text-stitch-fg-soft">
                Great job on your last session! Practice makes perfect. Start another interview to continue improving
                your skills.
              </p>
            </>
          )}

          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={handleNewSession}
            className="inline-flex items-center gap-2"
          >
            <MsIcon name="play_arrow" className="text-xl text-white" fill />
            <span>Start new interview</span>
          </Button>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            {
              iconName: 'insights' as const,
              title: 'Real-time feedback',
              desc: 'Get instant scores and detailed feedback on your answers to improve quickly.',
              bg: 'bg-stitch-accent-soft',
              color: 'text-stitch-accent',
            },
            {
              iconName: 'auto_awesome' as const,
              title: 'Personalized questions',
              desc: 'Questions tailored to your role, experience level, and target company.',
              bg: 'bg-stitch-success-soft',
              color: 'text-stitch-success',
            },
            {
              iconName: 'monitoring' as const,
              title: 'Track progress',
              desc: 'Monitor your improvement over time with detailed session summaries.',
              bg: 'bg-stitch-warning-soft',
              color: 'text-stitch-warning',
            },
          ].map((f) => (
            <div
              key={f.title}
              className="rounded-stitch-xl border border-stitch-border bg-stitch-surface p-6 text-center shadow-stitch-card"
            >
              <div
                className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full ${f.bg} ${f.color}`}
              >
                <MsIcon name={f.iconName} className="text-2xl" />
              </div>
              <h3 className="mb-2 font-semibold text-stitch-fg">{f.title}</h3>
              <p className="text-sm text-stitch-fg-soft">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <StartInterviewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onStartInterview={handleStartInterview}
      />
    </div>
  );
};
